import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import Delete from '@/assets/icons/delete.svg?react';
import Edit from '@/assets/icons/edit.svg?react';
import ReleaseNoteDescription from '@/components/manage-release-notes/release-note-description';
import ItemsLimitSelector from '@/components/shared/items-limit-selector';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Modal from '@/components/ui/modal';
import PaginationTabs from '@/components/ui/pagination-tabs';
import useDeleteReleaseNote from '@/hooks/use-delete-release-note';
import { getDateFnsLocale } from '@/utils/helpers/get-date-fns-locale';
import getReleaseNotePublishStatus from '@/utils/helpers/get-release-note-publish-status';

type ReleaseNote = {
  id: string | number;
  description: string;
  version: string;
  isPublished: boolean;
  isCurrent: boolean;
  releaseDate: Date;
};

type ReleaseNoteListProps = {
  releaseNoteLists?: ReleaseNote[];
  onEdit?: (id: string | number) => void;
  totalCount?: number;
  limit?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
};

const ReleaseNoteList = ({
  releaseNoteLists,
  onEdit,
  totalCount = 0,
  limit = 10,
  currentPage = 0,
  onPageChange = () => {},
  onLimitChange = () => {},
}: ReleaseNoteListProps) => {
  const { t } = useTranslation();

  const {
    mutate: deleteReleaseNote,
    isPending: isDeleting,
    variables: deletingId,
  } = useDeleteReleaseNote();

  const currentLocale = getDateFnsLocale();

  return (
    <div className="flex flex-col gap-4 text-sm w-full pb-8">
      {releaseNoteLists?.map(
        ({ id, description, version, isPublished, isCurrent, releaseDate }) => {
          const { badgeText, variant } = getReleaseNotePublishStatus({ isPublished, releaseDate });

          const formattedReleaseDate = format(releaseDate, 'dd MMM yyyy', {
            locale: currentLocale,
          });
          const modalTitle = isDeleting ? '' : t('release_note.modal.delete.header', { version });

          const isCurrentRowDeleting = isDeleting && deletingId === id;

          const onHandleEdit = () => {
            return onEdit?.(id);
          };

          return (
            <div
              key={id}
              className="flex flex-col gap-3 min-w-full [&_ul]:list-disc! [&_ul]:pl-5! [&_ul_li::marker]:text-black! [&_ol]:list-decimal! [&_ol]:pl-5! [&_ol_li::marker]:text-black! [&_li]:mb-1!"
            >
              <div className="flex justify-between w-full gap-3">
                <div className="flex flex-col gap-1">
                  <div className="flex gap-2 items-center flex-wrap">
                    <div className="font-semibold text-base">{version}</div>
                    {isCurrent && (
                      <span className="font-semibold text-base">({t('common.current')})</span>
                    )}
                    <Badge
                      variant={variant}
                      className="min-h-5 font-medium break-word whitespace-normal px-1 rounded-lg"
                    >
                      {badgeText}
                    </Badge>
                  </div>
                  <span className="text-muted-foreground text-xs">{formattedReleaseDate}</span>
                </div>
                <div className="flex gap-1">
                  <Button className="size-8" onClick={onHandleEdit}>
                    <Edit />
                  </Button>
                  <Modal
                    onConfirm={() => deleteReleaseNote(id)}
                    triggerer={<Delete />}
                    title={modalTitle}
                    isLoading={isCurrentRowDeleting}
                    acceptButtonLabel={t('actions.delete')}
                  >
                    {t('release_note.modal.delete.description')}
                  </Modal>
                </div>
              </div>
              <ReleaseNoteDescription description={description} />
            </div>
          );
        }
      )}
      {releaseNoteLists?.length && (
        <div className="flex items-center justify-between w-full mt-4">
          <ItemsLimitSelector totalCount={totalCount} limit={limit} onLimitChange={onLimitChange} />
          <PaginationTabs
            totalCount={totalCount}
            limit={limit}
            currentPage={currentPage}
            onPageChange={onPageChange}
            className="w-auto mx-0 justify-end"
          />
        </div>
      )}
    </div>
  );
};

export default ReleaseNoteList;
